import { revertAll } from '../store/actions';
import { persistor, store } from '../store/index';

import { clearStoragePreservingMigrationCookies } from './migration-cookies';

const TAB_SESSION_MARKER_KEY = 'app_tab_session_active';
const EXEMPT_REFERRER_SEGMENTS = ['/support', '/faq', '/payments'];

const normalizePathname = (pathname: string): string => {
    let end = pathname.length;
    while (end > 0 && pathname.charCodeAt(end - 1) === 47 /* '/' */) {
        end--;
    }
    return pathname.slice(0, end).toLowerCase() || '/';
};

function isSupportOrFaqReferrer(): boolean {
    try {
        if (!document.referrer) {
            return false;
        }

        const referrer = new URL(document.referrer);
        if (referrer.origin !== window.location.origin) {
            return false;
        }

        const referrerPath = normalizePathname(referrer.pathname);
        return EXEMPT_REFERRER_SEGMENTS.some(segment => referrerPath.includes(segment));
    } catch {
        return false;
    }
}

const getPersistedUserState = (): { isLoggedIn?: boolean } | null => {
    try {
        const persistedRoot = localStorage.getItem('persist:root');
        if (!persistedRoot) {
            return null;
        }

        const rootState = JSON.parse(persistedRoot);
        const userState = typeof rootState?.user === 'string' ? JSON.parse(rootState.user) : rootState?.user;
        return userState && typeof userState === 'object' ? userState : null;
    } catch {
        return null;
    }
};

/**
 * Clears client auth state and persisted Redux immediately (synchronous).
 * Used when the tab/window is unloading so the next visit shows the login page.
 * Does not call the logout API or redirect (the document is going away).
 */
export function clearPersistedClientSessionSync(): void {
    try {
        store.dispatch(revertAll());
    } catch {
        // ignore if store is unavailable during teardown
    }
    try {
        void persistor.purge();
    } catch {
        // ignore
    }
    clearStoragePreservingMigrationCookies();
}

export function markTabSessionActive(): void {
    sessionStorage.setItem(TAB_SESSION_MARKER_KEY, '1');
}

export function clearTabSessionMarker(): void {
    sessionStorage.removeItem(TAB_SESSION_MARKER_KEY);
}

const JUST_LOGGED_IN_KEY = 'app_just_logged_in';

/**
 * Marks that login just succeeded, so App.tsx can redirect to the post-login
 * setup screen from its own isLoggedIn effect (which only runs after the route
 * tree has already committed to PostAuthenticatedRoutes) instead of navigating
 * synchronously alongside the login dispatch, which races PostAuthenticatedRoutes'
 * own "/login -> ContractOverview" redirect for already-authenticated users.
 */
export function markJustLoggedIn(): void {
    sessionStorage.setItem(JUST_LOGGED_IN_KEY, '1');
}

/** Returns true and clears the flag if set — call exactly once per consumption. */
export function consumeJustLoggedInFlag(): boolean {
    const wasSet = sessionStorage.getItem(JUST_LOGGED_IN_KEY) === '1';
    if (wasSet) {
        sessionStorage.removeItem(JUST_LOGGED_IN_KEY);
    }
    return wasSet;
}

export function shouldClearPersistedSessionOnStartup(): boolean {
    const hasActiveTabMarker = sessionStorage.getItem(TAB_SESSION_MARKER_KEY) === '1';
    const isLoggedIn = getPersistedUserState()?.isLoggedIn === true;
    // Exception: allow new-tab opens from Support/FAQ links without forcing logout.
    if (isSupportOrFaqReferrer()) {
        return false;
    }
    return isLoggedIn && !hasActiveTabMarker;
}

export function clearPersistedSessionIfTabClosed(): void {
    if (shouldClearPersistedSessionOnStartup()) {
        clearPersistedClientSessionSync();
    }
}
